import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import {
  IconChevronDown,
  IconChevronRight,
  IconDatabase,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useChatQueryCsvDownload } from 'src/app/chat/useChatQueryCsvDownload';
import { DownloadActionIcon } from 'src/design/atoms/DownloadActionIcon';
import { IChatToolActivity } from 'src/hooks/useChat';

export interface ISqlDisclosureProps {
  activity: IChatToolActivity[];
  /** Enables the per-query CSV downloads; the server only exports queries of the caller's own conversation. */
  conversationId?: string | null;
  /** False while the reply streams or after it failed: the server saves the query records with the finished reply. */
  canExport?: boolean;
  /** run_sql calls in earlier tool lists of the same reply, so query numbers stay unique across the message. */
  queryNumberOffset?: number;
}

export const RUN_SQL_TOOL = 'run_sql';

const exportableAuditId = (item: IChatToolActivity): string | undefined =>
  item.name === RUN_SQL_TOOL &&
  item.status === 'done' &&
  !item.isError &&
  (item.rowCount ?? 0) > 0
    ? item.auditId
    : undefined;

const QueryCsvDownloadIcon: React.FC<{
  conversationId: string;
  auditId: string;
  queryNumber: number;
}> = ({ conversationId, auditId, queryNumber }) => {
  const { download, downloading } = useChatQueryCsvDownload(
    conversationId,
    auditId,
  );
  return (
    <DownloadActionIcon
      size={24}
      iconSize={14}
      variant="subtle"
      label="Download CSV"
      aria-label={`Download query ${queryNumber} as CSV`}
      loading={downloading}
      onClick={() => void download()}
    />
  );
};

/** @registry Collapsible list of SQL tool calls made while generating an assistant reply (row counts, truncation, running state, per-query CSV download); nested inside MessageBubble. */
export const SqlDisclosure = React.memo<ISqlDisclosureProps>(
  ({ activity, conversationId, canExport = false, queryNumberOffset = 0 }) => {
    const [expanded, setExpanded] = useState(false);
    const completed = activity.filter((a) => a.status === 'done');
    const running = activity.some((a) => a.status === 'running');

    return (
      <Stack gap={4}>
        <UnstyledButton
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          <Group gap={4}>
            {expanded ? (
              <IconChevronDown size={14} />
            ) : (
              <IconChevronRight size={14} />
            )}
            <IconDatabase size={14} />
            <Text size="xs" c="dimmed">
              {running
                ? 'Querying…'
                : `Ran ${completed.length} ${
                    completed.length === 1 ? 'query' : 'queries'
                  }`}
            </Text>
            {running && <Loader size="xs" />}
          </Group>
        </UnstyledButton>
        {expanded && (
          <Stack gap={4} pl="md">
            {activity.map((item, index) => {
              const auditId = exportableAuditId(item);
              return (
                <Group key={index} gap={6}>
                  <Text size="xs">{item.name}</Text>
                  {item.status === 'done' && item.rowCount !== undefined && (
                    <Badge size="xs" variant="light">
                      {item.rowCount} rows{item.truncated ? ' (truncated)' : ''}
                    </Badge>
                  )}
                  {canExport && conversationId && auditId && (
                    <QueryCsvDownloadIcon
                      conversationId={conversationId}
                      auditId={auditId}
                      queryNumber={
                        queryNumberOffset +
                        activity
                          .slice(0, index + 1)
                          .filter((a) => a.name === RUN_SQL_TOOL).length
                      }
                    />
                  )}
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  },
);
